import Link from "next/link"
import type { BreadcrumbCrumb } from "@/lib/breadcrumbs"

export type BreadcrumbsProps = {
  items: BreadcrumbCrumb[]
  /** When true, gold links + white current/separators for dark heroes. */
  darkMode?: boolean
  /** Dark mode on a light background: keeps gold links, current/separators turn dark. */
  lightBackground?: boolean
  className?: string
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 1.333 1.333 6.667V14h4.445V9.778h4.444V14h4.445V6.667L8 1.333Z" />
    </svg>
  )
}

/**
 * Page breadcrumb trail for hero placement.
 * Renders nothing when `items` is empty.
 */
export function Breadcrumbs({
  items,
  darkMode = false,
  lightBackground = false,
  className = "",
}: BreadcrumbsProps) {
  if (!items?.length) return null

  const whiteText = darkMode && !lightBackground
  const linkClass = darkMode
    ? "text-yellow-m10 hover:text-yellow-m10-hover focus-visible:outline-yellow-m10"
    : "text-blue-trmb hover:underline focus-visible:outline-blue-trmb"
  const mutedClass = whiteText ? "text-white" : "text-gray-m9"
  const separatorClass = whiteText ? "text-white" : "text-gray-m7"

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumbs"
      data-dark-mode={darkMode ? "true" : "false"}
      data-light-background={lightBackground ? "true" : "false"}
      className={`breadcrumbs text-sm leading-5 tracking-[0.14px] ${className}`}
    >
      <ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-1 p-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          const key = `${item.label}-${index}`

          return (
            <li key={key} className="flex items-center gap-x-2">
              {index > 0 && (
                <span className={separatorClass} aria-hidden="true">
                  /
                </span>
              )}

              {item.isHome && item.href ? (
                <Link
                  href={item.href}
                  aria-label={item.label || "Home"}
                  className={`inline-flex items-center ${linkClass}`}
                >
                  <HomeIcon />
                </Link>
              ) : item.href ? (
                <Link href={item.href} className={`no-underline ${linkClass}`}>
                  {item.label}
                </Link>
              ) : (
                <span
                  className={mutedClass}
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
